import * as React from 'react';
import { twMerge } from 'tailwind-merge';
import { Icon, type IconType } from './Icon';

type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
type ButtonSize = 'xs' | 'sm' | 'md' | 'lg';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: IconType;
  iconRight?: IconType;
  mono?: boolean;
}

const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  primary: 'bg-primary-9 text-[#1A0A06] border-primary-9 hover:bg-primary-10',
  secondary: 'bg-surface-2 text-text-1 border-stroke-2 hover:bg-surface-3',
  outline: 'bg-transparent text-text-1 border-stroke-2 hover:bg-surface-2',
  ghost: 'bg-transparent text-text-2 border-transparent hover:bg-surface-2',
  danger: 'bg-red-6 text-white border-red-6 hover:bg-red-9',
};

const SIZE_CLASSES: Record<ButtonSize, string> = {
  xs: 'text-[10px] px-1.5 py-0.5',
  sm: 'text-[11.5px] px-2 py-1',
  md: 'text-[13px] px-3 py-[7px]',
  lg: 'text-sm px-4 py-[9px]',
};

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = 'primary', size = 'md', icon, iconRight, mono = false, children, className, ...rest }, ref) => {
    return (
      <button
        ref={ref}
        className={twMerge(
          'inline-flex cursor-pointer items-center gap-1.5 rounded-[4px] border font-medium whitespace-nowrap transition-all duration-[140ms]',
          VARIANT_CLASSES[variant],
          SIZE_CLASSES[size],
          mono && 'font-mono tracking-[.06em] uppercase',
          className,
        )}
        {...rest}
      >
        {icon && <Icon symbol={icon} className="h-3.5 w-3.5" />}
        {children}
        {iconRight && <Icon symbol={iconRight} className="h-3.5 w-3.5" />}
      </button>
    );
  },
);
Button.displayName = 'Button';
